<script setup>
import { useNotificationStore } from "@/stores/useNotificationStore";
import { useAuthStore } from "@/stores/useAuthStore";
import { usePage } from "@inertiajs/inertia-vue3";
import { computed, onMounted, ref, watch } from "vue";
import axios from "axios";
import { Icon } from "@iconify/vue";
import moment from "moment";
import Loader from "@/Components/Loader.vue";

const store = useNotificationStore();
const authStore = useAuthStore();
const notifications = ref([]);
const selectedNotification = ref(null);
const showOverlay = ref(false);
const page = usePage();
const user = computed(() => page.props.value.auth.user);
const loading = ref({ list: false });
const parsedMessage = ref(null);

// récupère les notifs dès que l'user est dispo
watch(
  user,
  async (val) => {
    if (!val) return;

    authStore.setUser(val);

    try {
      loading.value.list = true;
      // Notifications déjà lues
      const resUnread = await axios.get(
        "/api/notifications/index_by_priority?to_read=true"
      );
      const unreadNotifications = resUnread.data.map((notif) => {
        const parsedData =
          typeof notif.data === "string" ? JSON.parse(notif.data) : notif.data;

        return {
          ...notif,
          data: parsedData,
          isRead: true,
        };
      });

      if (unreadNotifications.length) {
        selectedNotification.value =
          unreadNotifications[unreadNotifications.length - 1]; // auto sélection
        showOverlay.value = true;
      }

      // Notifications non lues
      const resRead = await axios.get("/api/notifications/index_by_priority");
      const readNotifications = resRead.data.map((notif) => {
        const parsedData =
          typeof notif.data === "string" ? JSON.parse(notif.data) : notif.data;

        return {
          ...notif,
          data: parsedData,
          isRead: false,
        };
      });

      // Fusion et tri par date (si tu veux, sinon juste concat)
      notifications.value = [...readNotifications, ...unreadNotifications].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des notifications :",
        error
      );
    } finally {
      loading.value.list = false;
    }
  },
  { immediate: true }
);

// Observer la notification sélectionnée et parser son contenu
watch(selectedNotification, (newNotif) => {
  if (newNotif?.data?.notif?.message) {
    parsedMessage.value = parseNotificationMessage(newNotif.data.notif.message);
  }
});

const markAsRead = async (notification) => {
  const notif = notifications.value.find(
    (notif) => notif.id === notification.id
  );

  if (notif) {
    try {
      notif.read_at = true; // Mise à jour locale
      axios.post(`/api/notifications/mark_as_read/${notif.id}`);

      // 👉 Trouver la prochaine notification non lue
      const nextNotif = notifications.value.find(
        (n) => !n.read_at && n.id !== notif.id
      );

      if (nextNotif) {
        selectedNotification.value = nextNotif;
      } else {
        // ✅ Toutes les notifications ont été lues
        showOverlay.value = false;
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la notification", error);
    }
  } else {
    console.log("Notification non trouvée");
  }
};

const parseNotificationMessage = (message) => {
  const blocks = message.blocks || [];
  const parsedContent = [];

  blocks.forEach((block) => {
    switch (block.type) {
      case "paragraph":
        parsedContent.push(`<p>${block.data.text}</p>`); // Texte des paragraphes
        break;

      case "image":
        parsedContent.push(
          `<img src="${block.data.file.url}" alt="${
            block.data.caption || "Image"
          }" class="w-11/12 mx-auto rounded-xl" />`
        ); // URL des images
        break;

      case "header":
        parsedContent.push(
          `<h${block.data.level} class="uppercase">${block.data.text}</h${block.data.level}>`
        ); // Titres avec niveaux dynamiques
        break;

      case "list":
        const listTag = block.data.style === "unordered" ? "ul" : "ol";
        const items = block.data.items
          .map((item) => `<li>${item.content}</li>`)
          .join("");
        parsedContent.push(`<${listTag}>${items}</${listTag}>`); // Listes ordonnées ou non
        break;

      case "checklist":
        parsedContent.push(
          block.data.items
            .map(
              (item) =>
                `<div><input type="checkbox" ${
                  item.meta.checked ? "checked" : ""
                } disabled /> ${item.content}</div>`
            )
            .join("")
        ); // Checklists avec cases à cocher
        break;

      case "table":
        const rows = block.data.content
          .map(
            (row) =>
              `<tr>${row
                .map((cell) => `<td class="border p-1">${cell}</td>`)
                .join("")}</tr>`
          )
          .join("");
        parsedContent.push(
          `<table class="border border-collapse w-full">${rows}</table>`
        ); // Tableaux HTML
        break;

      case "quote":
        parsedContent.push(
          `<blockquote class="font-bold italic">"${block.data.text}"<br/><small>- ${block.data.caption}</small></blockquote>`
        ); // Citations avec auteur
        break;

      default:
        console.warn(`Bloc de type inconnu : ${block.type}`);
    }
  });

  return parsedContent.join(""); // Retourne le contenu HTML combiné
};

const dismissOverlay = () => {
  showOverlay.value = false;
};
</script>

<template>
  <div
    v-if="showOverlay"
    class="fixed inset-0 bg-gray-900/70 z-[9999] flex items-center justify-center p-4"
  >
    <div
      class="w-full max-w-7xl h-full lg:h-[90vh] rounded-xl shadow-xl overflow-hidden border bg-primary flex flex-col lg:grid lg:grid-cols-3"
    >
      <!-- Liste gauche -->
      <div
        class="p-4 overflow-y-auto border-b lg:border-b-0 lg:border-r max-h-[40vh] lg:max-h-full"
      >
        <h2 class="text-lg font-bold mb-4 text-center uppercase">
          📬 Nouvelles notifications
        </h2>

        <div
          class="flex flex-col items-center mt-16 text-xs"
          v-if="loading.list"
        >
          <Loader size="16" />
          <p class="mt-2">Chargement des messages...</p>
        </div>

        <ul class="space-y-2">
          <li
            v-for="(notif, index) in notifications"
            :key="index"
            @click="selectedNotification = notif"
            :class="[
              'p-3 rounded cursor-pointer transition text-xs',
              selectedNotification?.id === notif.id ? 'bg-secondary' : '',
            ]"
          >
            <div class="flex items-center space-x-2">
              <Icon
                icon="ic:baseline-mark-email-read"
                v-if="notif?.read_at"
                class="text-green-500"
              />
              <p class="truncate">
                {{ notif.data?.notif?.title || "Notification" }}
              </p>
            </div>
            <div class="flex justify-end mt-2">
              <p class="text-xs">
                {{ moment(notif.created_at).format("DD/MM/y à HH[:]mm") }}
              </p>
            </div>
          </li>
        </ul>
      </div>

      <!-- Détail droite -->
      <div
        class="flex flex-col justify-between p-6 flex-1 overflow-y-auto col-span-2"
      >
        <div class="flex-1 overflow-y-auto">
          <h3 class="text-2xl my-8">
            {{ selectedNotification?.data?.notif?.title || "Titre" }}
          </h3>
          <div v-if="parsedMessage">
            <div v-html="parsedMessage" class="leading-7"></div>
          </div>
        </div>

        <div class="flex justify-end mt-6">
          <button
            v-if="!selectedNotification.read_at"
            @click="markAsRead(selectedNotification)"
            class="btn btn-primary"
          >
            J’ai lu et compris
          </button>
        </div>
      </div>
    </div>
  </div>
</template>


<style scoped>
body {
  overflow: hidden;
}
</style>
